import React, { useMemo, useCallback, useState } from 'react';
import { Card, Container } from 'react-bootstrap';
import { Column } from 'react-table';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader';
import { useNamespacedTranslation } from '../../../../hooks/useNamespacedTranslation';
import { useFlairBreadcrumbHook } from '../../../../hooks/useFlairBreadcrumbHook';
import ServerError from '../../../../components/ServerError';
import Loading from './Loading';
import { useJobsQuery } from '../../__generated__/graphql';
import { TableContentManual } from '../../manager/components/TableContentManual';
import { CareerOpportunitiesFilterTiles } from './CareerOpportunitiesFilterTiles';
import { JobFilter, emptyJobFilter, Option } from './types';
import { useHistory } from 'react-router-dom';
import { recruitmentRoutes } from '../routes';
import { EmptyStateCardBody } from '../../components/EmptyStateCard';
import GlobalTableFilter from '../../components/GlobalTableFilter';

type JobTableItem = {
  id: string;
  name: string;
  department: string | null;
  location: string | null;
  status: string | null;
};

const CareerOpportunities: React.FC = () => {
  const t = useNamespacedTranslation('navigation.menuItems.recruitment');
  const { t: tCommon } = useTranslation();
  const title = t('subMenuItems.careerOpportunities.title');
  const history = useHistory();

  const { data, loading, error } = useJobsQuery();
  const [filter, setFilter] = useState<JobFilter>(emptyJobFilter);
  const [searchText, setSearchText] = useState<string>('');

  useFlairBreadcrumbHook([{ label: t('title') }, { label: title }]);

  const handleOnRowClick = useCallback(
    (item: JobTableItem) => {
      history.push(
        recruitmentRoutes.jobDetails.route.create({ jobId: item.id }),
      );
    },
    [history],
  );

  const allItems: JobTableItem[] = useMemo(
    () =>
      (data?.jobs ?? []).map((job) => ({
        id: job.Id,
        name: job.Name,
        department: job.department?.Name ?? null,
        location: job.location?.Name ?? null,
        status: job.flair__Status__c ?? null,
      })),
    [data?.jobs],
  );

  //TODO:: check a generic way to build the options, there is a generic one.
  const departmentOptions: Option[] = useMemo(() => {
    const departments = new Set<string>();
    allItems.forEach((item) => {
      if (item.department) {
        departments.add(item.department);
      }
    });

    return Array.from(departments)
      .sort()
      .map((dept) => ({
        label: dept,
        value: dept,
      }));
  }, [allItems]);

  const locationOptions: Option[] = useMemo(() => {
    const locations = new Set<string>();
    allItems.forEach((item) => {
      if (item.location) {
        locations.add(item.location);
      }
    });

    return Array.from(locations)
      .sort()
      .map((loc) => ({
        label: loc,
        value: loc,
      }));
  }, [allItems]);

  const statusOptions: Option[] = useMemo(() => {
    const statuses = new Set<string>();
    allItems.forEach((item) => {
      if (item.status) {
        statuses.add(item.status);
      }
    });

    return Array.from(statuses)
      .sort()
      .map((status) => ({
        label: status,
        value: status,
      }));
  }, [allItems]);

  const items: JobTableItem[] = useMemo(() => {
    return allItems.filter((item) => {
      if (filter.department && item.department !== filter.department) {
        return false;
      }
      if (filter.location && item.location !== filter.location) {
        return false;
      }
      if (filter.status && item.status !== filter.status) {
        return false;
      }
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        const matchesSearch =
          item.name?.toLowerCase().includes(searchLower) ||
          item.department?.toLowerCase().includes(searchLower) ||
          item.location?.toLowerCase().includes(searchLower) ||
          item.status?.toLowerCase().includes(searchLower);
        if (!matchesSearch) {
          return false;
        }
      }
      return true;
    });
  }, [allItems, filter, searchText]);

  const handleSearchChange = useCallback((text: string) => {
    setSearchText(text);
  }, []);

  //TODO:: sorting doesn't work
  const columns: Column<JobTableItem>[] = useMemo(
    () => [
      {
        Header: () => tCommon('recruitment.careerOpportunities.name'),
        accessor: 'name',
        Cell: ({ value }: { value: string }) => value,
        sortType: 'caseInsensitive',
      },
      {
        Header: () => tCommon('recruitment.careerOpportunities.department'),
        accessor: 'department',
        Cell: ({ value }: { value: string | null }) => value ?? '-',
        sortType: 'caseInsensitive',
      },
      {
        Header: () => tCommon('recruitment.careerOpportunities.location'),
        accessor: 'location',
        Cell: ({ value }: { value: string | null }) => value ?? '-',
        sortType: 'caseInsensitive',
      },
      {
        Header: () => tCommon('recruitment.careerOpportunities.status'),
        accessor: 'status',
        Cell: ({ value }: { value: string | null }) => value ?? '-',
        sortType: 'caseInsensitive',
      },
    ],
    [tCommon],
  );

  if (error) {
    return <ServerError />;
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <Container>
      <PageHeader title={title} />
      <CareerOpportunitiesFilterTiles
        filter={filter}
        departmentOptions={departmentOptions}
        locationOptions={locationOptions}
        statusOptions={statusOptions}
        onChange={setFilter}
      />
      <Card>
        <Card.Header>
          <div className="d-flex align-items-center small">
            <GlobalTableFilter
              placeholder={tCommon('recruitment.careerOpportunities.search')}
              onFilterChange={handleSearchChange}
            />
          </div>
        </Card.Header>
        {items.length === 0 ? (
          <EmptyStateCardBody
            title={tCommon('recruitment.careerOpportunities.noJobs')}
          />
        ) : (
          <TableContentManual
            columns={columns}
            data={items}
            onRowClick={handleOnRowClick}
            getRowProps={() => ({ role: 'button' })}
          />
        )}
      </Card>
    </Container>
  );
};

export default CareerOpportunities;
